/**
 * True when a lazy route failed because its chunk URL is missing — typical after
 * a new deploy while an older tab still runs the previous bundle.
 */
export function isLikelyStaleChunkError(error: unknown): boolean {
    if (!error) return false;
    const msg = error instanceof Error ? error.message : String(error);
    const name = error instanceof Error ? error.name : '';
    return (
        name === 'ChunkLoadError' ||
        /Loading chunk \d+ failed/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg)
    );
}

/** Vite HMR can briefly desync context modules from their Provider after a hot update. */
export function isAuthProviderDesyncError(error: unknown): boolean {
    if (!error) return false;
    const msg = error instanceof Error ? error.message : String(error);
    return (
        /useAuth must be used within an AuthProvider/i.test(msg) ||
        /usePermissionsContext must be used within a PermissionsProvider/i.test(msg) ||
        /useOrganization must be used within an OrganizationProvider/i.test(msg) ||
        /must be used within a (Auth|Permissions|Organization|Config|Language|Theme|Notification)Provider/i.test(
            msg,
        )
    );
}

/**
 * Timestamp of last auto-reload for stale chunks. Prevents tight reload loops if a
 * chunk stays broken; after a minute another auto-reload is allowed (e.g. flaky CDN).
 */
export const CHUNK_RELOAD_TS_KEY = 'spa_chunk_reload_ts';

const COOLDOWN_MS = 60_000;

/** In dev, always recover from context Provider HMR desync with a full reload. */
export function shouldAutoReloadForAuthDesync(): boolean {
    try {
        return import.meta.env.DEV === true
    } catch {
        return false
    }
}

/** True if we should auto-reload once (stale deploy); false if we already tried recently. */
export function shouldAutoReloadForStaleChunk(): boolean {
    try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_TS_KEY) || '0');
        const now = Date.now();
        if (last && now - last < COOLDOWN_MS) return false;
        sessionStorage.setItem(CHUNK_RELOAD_TS_KEY, String(now));
        return true;
    } catch {
        return true;
    }
}
